import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../lib/api';
import type { WaitingItem } from '../lib/types';
import {
  mergeWaitingSeen,
  newestWaiting,
  parseSeenUntil,
  readWaitingSeen,
  WAITING_SEEN_EVENT,
  waitingSeenKey,
  writeWaitingSeen,
} from '../lib/waiting';

type SeenResponse = { synced: boolean; seenUntil: string | null };

const SEEN_KEY = ['waiting', 'seen'] as const;

/**
 * „Čaká na mňa“ + to, čo z toho už človek videl.
 *
 * Používa ho odznak v navigácii aj samotná Schránka, preto zdieľaný kľúč
 * `['waiting']`: dopyt ide raz, nie dvakrát. Obnovuje ho známka zmeny
 * (`useChangeMarks`) — interval tu je iba záchytná sieť, preto je dlhý.
 *
 * „Videné“ má dve pamäte: localStorage (hneď, aj offline) a server (spoločné
 * pre všetky zariadenia). Platí väčšia z nich. Kým server tabuľku nemá
 * (`synced: false`), nič sa naň neposiela a správa sa to ako predtým.
 */
export const useWaiting = (userId: number | undefined) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['waiting'],
    queryFn: () => api.inboxWaiting(),
    enabled: userId !== undefined,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });

  // Kľúč pod ['waiting'], takže známka zmeny ho obnoví spolu so zoznamom — to
  // je aj chvíľa, keď sa oplatí pozrieť, či človek medzitým niečo neprečítal
  // na inom zariadení.
  const seenQuery = useQuery({
    queryKey: SEEN_KEY,
    queryFn: () => api.inboxWaitingSeen(),
    enabled: userId !== undefined,
    staleTime: 60_000,
    // Výpadok servera nesmie nič zablokovať: appka ide ďalej s miestnou
    // hodnotou, takže opakovanie by iba oddialilo `seenReady`.
    retry: false,
  });

  const items = useMemo<WaitingItem[]>(() => query.data?.items ?? [], [query.data]);
  const [localSeen, setLocalSeen] = useState(() => readWaitingSeen(userId));
  const synced = seenQuery.data?.synced === true;
  const serverSeen = synced ? seenQuery.data?.seenUntil : null;
  const seenAt = mergeWaitingSeen(localSeen, serverSeen);
  // Kým sa server nevyjadril, „videné“ ešte nie je isté. Zoznam s tým čaká so
  // zvýraznením aj s označením za videné — inak by rozsvietil, čo človek
  // prečítal na telefóne, a hneď by to aj zapísal ako práve videné tu.
  const seenReady = userId === undefined || seenQuery.status !== 'pending';

  // Odznak a Schránka sú dve inštancie tohto hooku. Keď jedna posunie
  // „videné“, druhá sa to musí dozvedieť hneď, inak by odznak svietil ďalej,
  // hoci človek práve pozerá zoznam. `storage` pokrýva ostatné karty.
  useEffect(() => {
    setLocalSeen(readWaitingSeen(userId));
    const sync = () => setLocalSeen(readWaitingSeen(userId));
    const onStorage = (event: StorageEvent) => {
      if (event.key === waitingSeenKey(userId)) {
        sync();
      }
    };
    window.addEventListener(WAITING_SEEN_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(WAITING_SEEN_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, [userId]);

  const push = useCallback(
    (value: number) => {
      api
        .inboxWaitingSeenUpdate(new Date(value).toISOString())
        .then((response) => queryClient.setQueryData<SeenResponse>(SEEN_KEY, response))
        .catch(() => {
          // Miestna hodnota je zapísaná; server to dostane pri ďalšom posune
          // alebo pri zladení nižšie. Kvôli odznaku netreba nikoho vyrušovať.
        });
    },
    [queryClient],
  );

  // Zladenie oboch pamätí, keď príde odpoveď servera. Server vpredu = človek
  // čítal inde, prevezme sa do localStorage. Miestna vpredu = videl niečo
  // tu, kým server tabuľku ešte nemal alebo nebol dosiahnuteľný — pošle sa.
  const serverMs = parseSeenUntil(serverSeen);
  useEffect(() => {
    if (!synced || userId === undefined) return;
    const local = readWaitingSeen(userId);
    if (serverMs > local) {
      writeWaitingSeen(userId, serverMs);
      window.dispatchEvent(new Event(WAITING_SEEN_EVENT));
    } else if (local > serverMs) {
      push(local);
    }
  }, [synced, serverMs, userId, push]);

  const markSeen = useCallback(() => {
    const newest = newestWaiting(items);
    // Iba dopredu. Keby sa „videné“ vrátilo (napr. keď najnovšia položka
    // zmizla, lebo niekto zahlasoval), staré veci by sa rozsvietili nanovo.
    if (newest <= Math.max(readWaitingSeen(userId), serverMs)) {
      return;
    }
    writeWaitingSeen(userId, newest);
    setLocalSeen(newest);
    window.dispatchEvent(new Event(WAITING_SEEN_EVENT));
    if (synced) {
      push(newest);
    }
  }, [items, userId, serverMs, synced, push]);

  return {
    items,
    seenAt,
    seenReady,
    markSeen,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
};
